import { useEffect, useMemo, useRef, useState } from 'react';
import { inject } from 'mobx-react';
import { Badge, IconButton, Popover } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { parseComments, summarizeNewest } from './parse-comments';
import { commentsRequest, hasExpenseCommentIdentity, commentErrorKey } from '../../../utils/comments-api';
import CommentsPanel from '../../../components/comments/CommentsPanel';
import './comments-badge.scss';

const AUTO_DISMISS_MS = 5000;
// Stable id so paging between expenses replaces the toast instead of stacking.
const TOAST_ID = 'expense-comments';

const LegacyCommentsBadge = ({ data, comments }) => {
  const rows = useMemo(() => parseComments(comments), [comments]);
  const anchorRef = useRef(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const summary = rows.length > 0
    ? summarizeNewest(rows, { internalLabel: data.t.read('review_expense.comments.internal') })
    : '';

  const showToast = () => toast(summary, { id: TOAST_ID, icon: '💬', duration: AUTO_DISMISS_MS });

  useEffect(() => {
    if (summary) showToast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  if (rows.length === 0) return null;

  return (
    <>
      <IconButton
        ref={anchorRef}
        size='small'
        onClick={() => { setPopoverOpen(true); showToast(); }}
        data-testid='comments-badge'
        aria-label={data.t.read('review_expense.comments.aria_label')}
      >
        <Badge badgeContent={rows.length} color='primary' data-testid='comments-badge-count'>
          <FontAwesomeIcon icon={faCommentDots} />
        </Badge>
      </IconButton>

      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={() => setPopoverOpen(false)}
        // Left/left: the badge sits at the far left, so a right-anchored panel
        // grows over the sidebar instead of into the space beside Back.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { 'aria-labelledby': 'comments-popover-title' } }}
      >
        <div className='comments-popover' data-testid='comments-popover'>
          <div id='comments-popover-title' className='comments-popover__header'>
            {data.t.read('review_expense.comments.title')}
          </div>
          <ul className='comments-popover__list'>
            {rows.map((row, i) => (
              <li
                key={i}
                className={[
                  'comments-popover__row',
                  row.author === 'INTERNAL' && 'comments-popover__row--internal',
                  !row.timestamp && !row.author && 'comments-popover__row--free',
                ].filter(Boolean).join(' ')}
              >
                {row.timestamp && <span className='comments-popover__ts'>{row.timestamp}</span>}
                {row.author && (
                  <span className='comments-popover__author'>
                    {row.author === 'INTERNAL' ? data.t.read('review_expense.comments.internal') : row.author}
                  </span>
                )}
                <span className='comments-popover__text'>
                  {row.text}
                  {row.count > 1 && <span className='comments-popover__count'> × {row.count}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Popover>
    </>
  );
};

// The badge reads a compact summary; the full, writable discussion is requested only when opened.
// State is tied to receipt and actor so navigation never renders the old preview.
const ServiceCommentsBadge = ({ data, expense }) => {
  const identity = [expense.expense_uid, expense.updated_at, data.id_client, data.id_user, data.role].join(':');
  const [state, setState] = useState({ identity: null });
  const [open, setOpen] = useState(false);
  const anchor = useRef(null);
  const generation = useRef(0);
  const current = state.identity === identity;
  const summary = current ? state.summary : null;
  const error = current ? state.error : null;
  const reload = async () => {
    const token = ++generation.current;
    setState({ identity, loading: true });
    try {
      const response = await commentsRequest('/comments/summaries', 'POST', {
        subjects: [{ type: 'expense', id: expense.expense_uid }],
      });
      if (token !== generation.current) return;
      const value = response.summaries?.[0];
      if (!value || value.unavailable) setState({ identity, error: 'error_comments_access' });
      else setState({ identity, summary: value });
    } catch (caught) {
      if (token === generation.current) setState({ identity, error: commentErrorKey(caught) });
    }
  };
  useEffect(() => {
    setOpen(false);
    toast.dismiss(TOAST_ID);
    reload();
    return () => { generation.current += 1; toast.dismiss(TOAST_ID); };
    // Reload only when the receipt or authorization identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);
  useEffect(() => {
    if (summary?.latest) toast([summary.latest.author?.display_name, summary.latest.body].filter(Boolean).join(' · '),
      { id: TOAST_ID, icon: '💬', duration: AUTO_DISMISS_MS });
  }, [summary]);
  if (!current || state.loading) return <small role="status">{data.t.read('comment_ui.loading')}</small>;
  if (error) return <div role="alert"><small>{data.t.read(error)}</small>
    <button type="button" className="btn btn-link btn-sm" onClick={reload}>{data.t.read('comment_ui.refresh')}</button></div>;
  // The icon stays with no comments yet, so accounting can add the first one.
  return <>
    <IconButton ref={anchor} size="small" data-testid="comments-badge" aria-label={data.t.read('review_expense.comments.aria_label')} onClick={() => setOpen(true)}>
      <Badge badgeContent={summary.count} max={999} color="primary" data-testid="comments-badge-count"><FontAwesomeIcon icon={faCommentDots} /></Badge>
    </IconButton>
    <Popover open={current && open} anchorEl={anchor.current} onClose={() => { setOpen(false); reload(); }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
      <div className="comments-popover" data-testid="comments-popover">
        <CommentsPanel invoiceId={expense.expense_uid} subjectType="expense" refreshKey={expense.json} data={data} />
      </div>
    </Popover>
  </>;
};

const CommentsBadge = ({ data, expense, comments }) => hasExpenseCommentIdentity(expense)
  ? <ServiceCommentsBadge data={data} expense={expense} />
  : <LegacyCommentsBadge data={data} comments={comments} />;
export default inject('data')(CommentsBadge);
